// FILE: src/hooks/useDriftKnowledgeBase.ts
import React from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;

import { getApiHeaders } from '../support/queries';
import { DriftPipelineFile } from '../types/aiTypes';

type DriftKnowledgeBaseOptions = {
  pipelineId?: string | null;
  enabled?: boolean;
  refetchInterval?: number | false;
  managedUpload?: boolean;
};

const apiBase = process.env.REACT_APP_AI_API || process.env.REACT_APP_API || '';

const TERMINAL_STATES = [
  'completed',
  'complete',
  'succeeded',
  'success',
  'failed',
  'failure',
  'error',
  'cancelled',
  'canceled',
];

function normalizeEndpoint(endpoint: string) {
  let p = endpoint || '';

  try {
    if (p.startsWith('http://') || p.startsWith('https://')) {
      p = new URL(p).pathname;
    }
  } catch {
    // noop
  }

  p = p.replace(/^\/+/, '');
  if (p.startsWith('api/')) p = p.slice(4);

  return p;
}

function buildApiUrl(endpoint: string, params: Record<string, any> = {}) {
  const normalized = normalizeEndpoint(endpoint);
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });

  const qs = query.toString();
  return qs ? `${apiBase}/${normalized}?${qs}` : `${apiBase}/${normalized}`;
}

async function apiGet<T = any>(endpoint: string, params: Record<string, any> = {}): Promise<T> {
  const response = await fetch(buildApiUrl(endpoint, params), {
    method: 'GET',
    headers: getApiHeaders(null, 'GET', endpoint),
    credentials: 'include',
  });

  if (!response.ok) {
    let detail = '';

    try {
      detail = await response.text();
    } catch {
      detail = '';
    }

    throw new Error(detail || `GET ${endpoint} failed`);
  }

  return response.json();
}

async function apiPost<T = any>(endpoint: string, params: Record<string, any> = {}): Promise<T> {
  const response = await fetch(buildApiUrl(endpoint, params), {
    method: 'POST',
    headers: getApiHeaders(null, 'POST', endpoint),
    credentials: 'include',
  });

  if (!response.ok) {
    let detail = '';

    try {
      detail = await response.text();
    } catch {
      detail = '';
    }

    throw new Error(detail || `POST ${endpoint} failed`);
  }

  return response.json();
}

async function apiPostForm<T = any>(
  endpoint: string,
  formData: FormData,
  params: Record<string, any> = {},
): Promise<T> {
  const headers = getApiHeaders(null, 'POST', endpoint);
  delete (headers as any)['Content-Type'];

  const response = await fetch(buildApiUrl(endpoint, params), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    let detail = '';

    try {
      detail = await response.text();
    } catch {
      detail = '';
    }

    throw new Error(detail || `POST ${endpoint} failed`);
  }

  return response.json();
}

function firstArrayItem(value: any): any {
  if (Array.isArray(value)) {
    return value[0] || {};
  }

  return value;
}

function unwrapPipeline(payload: any): any {
  if (!payload) return {};

  if (payload?.data !== undefined) {
    return firstArrayItem(payload.data);
  }

  if (payload?.pipeline !== undefined) {
    return firstArrayItem(payload.pipeline);
  }

  if (payload?.result !== undefined) {
    return firstArrayItem(payload.result);
  }

  return firstArrayItem(payload);
}

function normalizeFile(file: any, index: number): DriftPipelineFile {
  return {
    ...file,
    filename:
      file.filename ||
      file.file_name ||
      file.name ||
      file.path ||
      `file-${index}`,
    path:
      file.path ||
      file.filename ||
      file.file_name ||
      file.name,
    size: Number(
      file.size ||
      file.file_size ||
      file.bytes ||
      file.content_length ||
      0,
    ),
    content_type:
      file.content_type ||
      file.mime_type ||
      file.type,
    updated_at:
      file.updated_at ||
      file.last_updated ||
      file.last_modified ||
      file.modified_at,
    ingestion_status:
      file.ingestion_status ||
      file.status,
  };
}

function extractFilesFromListPipelineFilesResponse(payload: any): DriftPipelineFile[] {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload.map((file, index) => normalizeFile(file, index));
  }

  if (Array.isArray(payload?.data)) {
    return payload.data.map((file: any, index: number) => normalizeFile(file, index));
  }

  if (Array.isArray(payload?.data?.files)) {
    return payload.data.files.map((file: any, index: number) => normalizeFile(file, index));
  }

  if (Array.isArray(payload?.files)) {
    return payload.files.map((file: any, index: number) => normalizeFile(file, index));
  }

  return [];
}

function extractFilesFromPipelineMetadata(payload: any): DriftPipelineFile[] {
  const pipeline = unwrapPipeline(payload);

  const candidates = [
    pipeline?.components?.loader?.files,
    pipeline?.loader?.files,
    pipeline?.files,
    payload?.files,
    payload?.data?.files,
    Array.isArray(payload?.data) ? payload.data?.[0]?.components?.loader?.files : undefined,
    Array.isArray(payload?.data) ? payload.data?.[0]?.loader?.files : undefined,
    Array.isArray(payload?.data) ? payload.data?.[0]?.files : undefined,
    Array.isArray(payload?.pipeline) ? payload.pipeline?.[0]?.components?.loader?.files : undefined,
    Array.isArray(payload?.result) ? payload.result?.[0]?.components?.loader?.files : undefined,
  ];

  const files = candidates.find(Array.isArray) || [];

  return files.map((file: any, index: number) => normalizeFile(file, index));
}

function extractFiles(filesPayload: any, pipelinePayload: any): DriftPipelineFile[] {
  const listedFiles = extractFilesFromListPipelineFilesResponse(filesPayload);

  if (listedFiles.length) {
    return listedFiles;
  }

  return extractFilesFromPipelineMetadata(pipelinePayload);
}

function extractTaskId(payload: any): string | null {
  return (
    payload?.task_id ||
    payload?.id ||
    payload?.job_id ||
    payload?.data?.task_id ||
    payload?.data?.id ||
    payload?.result?.task_id ||
    payload?.result?.upload_response?.task_id ||
    payload?.steps?.[0]?.result?.task_id ||
    payload?.steps?.[0]?.result?.upload_response?.task_id ||
    null
  );
}

function extractExecutionId(payload: any): string | null {
  return (
    payload?.execution_id ||
    payload?.executionId ||
    payload?.pipeline_executionid ||
    payload?.pipeline_execution_id ||
    payload?.id ||
    payload?.data?.execution_id ||
    payload?.data?.executionId ||
    payload?.data?.pipeline_executionid ||
    payload?.data?.id ||
    payload?.result?.execution_id ||
    payload?.result?.pipeline_executionid ||
    payload?.result?.ingestion_response?.pipeline_executionid ||
    payload?.steps?.[0]?.result?.execution_id ||
    payload?.steps?.[0]?.result?.pipeline_executionid ||
    payload?.steps?.[0]?.result?.ingestion_response?.pipeline_executionid ||
    null
  );
}

function extractState(payload: any): string {
  return String(
    payload?.status ||
    payload?.current_status ||
    payload?.state ||
    payload?.ingestion_status ||
    '',
  ).toLowerCase();
}

function isTerminalState(payload: any): boolean {
  const state = extractState(payload);
  return TERMINAL_STATES.includes(state);
}

export function useDriftKnowledgeBase(options: DriftKnowledgeBaseOptions = {}) {
  const {
    pipelineId,
    enabled = true,
    refetchInterval = false,
    managedUpload = true,
  } = options;

  const [taskId, setTaskId] = React.useState<string | null>(null);
  const [executionId, setExecutionId] = React.useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = React.useState<any>(null);
  const [ingestionStatus, setIngestionStatus] = React.useState<any>(null);
  const [managedUploadStatus, setManagedUploadStatus] = React.useState<any>(null);

  React.useEffect(() => {
    setTaskId(null);
    setExecutionId(null);
    setUploadStatus(null);
    setIngestionStatus(null);
    setManagedUploadStatus(null);
  }, [pipelineId]);

  const pipelineQuery = useQuery<any, Error>({
    queryKey: ['drift-kb', 'pipeline', pipelineId || 'default'],
    queryFn: () =>
      apiGet<any>('ai/drift/pipeline/', {
        ...(pipelineId ? { pipeline_id: pipelineId } : {}),
      }),
    ...{
        enabled,
        refetchInterval,
        placeholderData: keepPreviousData,
      },
  });

  const filesQuery = useQuery<any, Error>({
    queryKey: ['drift-kb', 'pipeline-files', pipelineId || 'default'],
    queryFn: () =>
      apiGet<any>('ai/drift/pipeline/files/', {
        ...(pipelineId ? { pipeline_id: pipelineId } : {}),
      }),
    ...{
        enabled: enabled && Boolean(pipelineId),
        refetchInterval,
        placeholderData: keepPreviousData,
      },
  });

  const taskStatusQuery = useQuery<any, Error>({
    queryKey: ['drift-kb', 'upload-status', taskId],
    queryFn: () => apiGet<any>(`ai/drift/status/${taskId}/`),
    enabled: Boolean(taskId),
    refetchInterval: taskId ? 5000 : false,
  });

  const refetchFiles = filesQuery.refetch;
  const refetchPipeline = pipelineQuery.refetch;

  const ingestionStatusQuery = useQuery<any, Error>({
    queryKey: ['drift-kb', 'ingestion-status', executionId],
    queryFn: () => apiGet<any>(`ai/drift/ingestion/${executionId}/`),
    enabled: Boolean(executionId),
    refetchInterval: executionId ? 5000 : false,
  });

  React.useEffect(() => {
    const data = taskStatusQuery.data;
    if (data === undefined) return;

    setUploadStatus(data);
    if (isTerminalState(data)) {
      setTaskId(null);
      void (async () => {
        await refetchFiles();
        await refetchPipeline();
      })();
    }
  }, [taskStatusQuery.dataUpdatedAt, taskStatusQuery.data, refetchFiles, refetchPipeline]);

  React.useEffect(() => {
    const data = ingestionStatusQuery.data;
    if (data === undefined) return;

    setIngestionStatus(data);
    if (isTerminalState(data)) {
      setExecutionId(null);
      void (async () => {
        await refetchFiles();
        await refetchPipeline();
      })();
    }
  }, [ingestionStatusQuery.dataUpdatedAt, ingestionStatusQuery.data, refetchFiles, refetchPipeline]);

  const ingestMutation = useMutation<any, Error, void>({
    mutationFn: async () =>
      apiPost<any>('ai/drift/ingest/', {
        ...(pipelineId ? { pipeline_id: pipelineId } : {}),
      }),
    onSuccess: (data) => {
      setIngestionStatus(data);

      const nextExecutionId = extractExecutionId(data);
      if (nextExecutionId) {
        setExecutionId(nextExecutionId);
      }
    },
  });

  const legacyUploadMutation = useMutation<any, Error, File[]>({
    mutationFn: async (files) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      return apiPostForm<any>('ai/drift/upload/', formData, {
        ...(pipelineId ? { pipeline_id: pipelineId } : {}),
      });
    },
    onSuccess: async (data) => {
      setUploadStatus(data);

      const nextTaskId = extractTaskId(data);
      if (nextTaskId) {
        setTaskId(nextTaskId);
      }

      try {
        const ingestResp = await ingestMutation.mutateAsync();
        const nextExecutionId = extractExecutionId(ingestResp);

        if (nextExecutionId) {
          setExecutionId(nextExecutionId);
        }
      } catch {
        // ingestion error is exposed by ingestMutation
      }
    },
  });

  const managedUploadMutation = useMutation<any, Error, File[]>({
    mutationFn: async (files) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      return apiPostForm<any>('ai/rag/drift/managed-upload/', formData, {
        ...(pipelineId ? { pipeline_id: pipelineId } : {}),
      });
    },
    onSuccess: async (data) => {
      setManagedUploadStatus(data);
      setUploadStatus(data);

      const nextTaskId = extractTaskId(data);
      if (nextTaskId) {
        setTaskId(nextTaskId);
      }

      const nextExecutionId = extractExecutionId(data);
      if (nextExecutionId) {
        setExecutionId(nextExecutionId);
      }

      await filesQuery.refetch();
      await pipelineQuery.refetch();
    },
  });

  const uploadFiles = async (files: File[]) => {
    if (managedUpload) {
      return managedUploadMutation.mutateAsync(files);
    }

    return legacyUploadMutation.mutateAsync(files);
  };

  const refresh = async () => {
    await pipelineQuery.refetch();
    await filesQuery.refetch();

    if (taskId) {
      await taskStatusQuery.refetch();
    }

    if (executionId) {
      await ingestionStatusQuery.refetch();
    }
  };

  const pipeline = unwrapPipeline(pipelineQuery.data);
  const files = extractFiles(filesQuery.data, pipelineQuery.data);

  const uploading = legacyUploadMutation.isPending || managedUploadMutation.isPending;
  const uploadError = legacyUploadMutation.error || managedUploadMutation.error;

  return {
    pipelineId: pipelineId || '',
    pipeline,
    pipelineRaw: pipelineQuery.data,
    files,
    filesRaw: filesQuery.data,
    pipelineLoading:
      pipelineQuery.isLoading ||
      pipelineQuery.isFetching ||
      filesQuery.isLoading ||
      filesQuery.isFetching,
    pipelineError: pipelineQuery.error || filesQuery.error,
    taskId,
    executionId,
    uploadStatus,
    ingestionStatus,
    managedUploadStatus,
    uploading,
    ingesting: ingestMutation.isPending || managedUploadMutation.isPending,
    uploadError,
    ingestionError: ingestMutation.error || managedUploadMutation.error,
    refresh,
    uploadFiles,
    startIngestion: ingestMutation.mutateAsync,
  };
}

export default useDriftKnowledgeBase;